export type JsonSchema = Record<string, unknown>;

export function getSchemaType(schema: JsonSchema): string | undefined {
  const type = schema.type;
  if (typeof type === 'string') return type;
  if (Array.isArray(type)) {
    return type.find((value): value is string => typeof value === 'string');
  }
  return undefined;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deriveArrayDefault(schema: JsonSchema): unknown[] {
  const itemSchema = isRecord(schema.items) ? schema.items : undefined;
  if (!itemSchema) return [];

  const minItems =
    typeof schema.minItems === 'number' && Number.isFinite(schema.minItems)
      ? schema.minItems
      : 0;

  return Array.from({ length: Math.max(0, minItems) }, () =>
    defaultFromJsonSchema(itemSchema),
  );
}

function pickUnionSchema(schema: JsonSchema): JsonSchema | undefined {
  const unionSchemas = (schema.oneOf ?? schema.anyOf) as unknown;
  if (!Array.isArray(unionSchemas)) return undefined;
  const firstSchema = unionSchemas.find(isRecord);
  return firstSchema;
}

export function defaultFromJsonSchema(schema: JsonSchema): unknown {
  if ('default' in schema) return schema.default;
  if ('const' in schema) return schema.const;

  const unionSchema = pickUnionSchema(schema);
  if (unionSchema) return defaultFromJsonSchema(unionSchema);

  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return schema.enum[0];
  }

  const schemaType = getSchemaType(schema);

  switch (schemaType) {
    case 'string':
      return '';
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return deriveArrayDefault(schema);
    case 'object': {
      const properties = isRecord(schema.properties)
        ? schema.properties
        : undefined;
      if (!properties) return {};

      const required = new Set(
        Array.isArray(schema.required)
          ? schema.required.filter(
              (value): value is string => typeof value === 'string',
            )
          : [],
      );
      const obj: Record<string, unknown> = {};
      for (const [key, propSchema] of Object.entries(properties)) {
        if (!isRecord(propSchema)) continue;
        const hasDefault =
          'default' in propSchema ||
          'const' in propSchema ||
          Array.isArray(propSchema.enum) ||
          propSchema.oneOf !== undefined ||
          propSchema.anyOf !== undefined;
        if (hasDefault || required.has(key)) {
          obj[key] = defaultFromJsonSchema(propSchema);
        }
      }
      return obj;
    }
    default:
      return null;
  }
}

export function buildConfigDefaults(
  configSchema?: Record<string, unknown>,
): Record<string, unknown> {
  if (!configSchema) return {};
  const properties = isRecord(configSchema.properties)
    ? configSchema.properties
    : undefined;
  if (!properties) return {};

  const defaults: Record<string, unknown> = {};
  for (const [key, propSchema] of Object.entries(properties)) {
    if (!isRecord(propSchema)) continue;
    const hasDefault =
      'default' in propSchema ||
      'const' in propSchema ||
      Array.isArray(propSchema.enum) ||
      propSchema.oneOf !== undefined ||
      propSchema.anyOf !== undefined;
    const required = Array.isArray(configSchema.required)
      ? configSchema.required.includes(key)
      : false;
    if (hasDefault || required) {
      defaults[key] = defaultFromJsonSchema(propSchema);
    }
  }
  return defaults;
}
